import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { authService } from '../../services/auth/auth-service.js'
import type { PlanTier } from '../../services/auth/auth-context.js'
import {
  paywallUrl,
  manageUrl,
  openExternal,
} from '../../services/subscription/subscription-api.js'
import { Capacitor } from '@capacitor/core'
import '@material/web/progress/linear-progress.js'

interface TierInfo {
  name: string
  tagline: string
  features: string[]
}

const TIER_INFO: Record<PlanTier, TierInfo> = {
  free: {
    name: 'Free',
    tagline: 'The essentials for getting out there.',
    features: ['Topographic base maps', 'GPS tracking', 'Trip planning', 'Safety & hazard data'],
  },
  basecamp: {
    name: 'Basecamp',
    tagline: 'Explore with confidence.',
    features: [
      'Everything in Free',
      'Offline maps',
      'Cloud sync across devices',
      'Route export (GPX/KML)',
    ],
  },
  alpine: {
    name: 'Alpine',
    tagline: 'Leave nothing to chance.',
    features: [
      'Everything in Basecamp',
      'Premium map layers',
      '3D terrain & globe',
      'High-res satellite imagery',
      'Weather overlays & forecasts',
    ],
  },
}

@customElement('cairn-subscription')
export class CairnSubscription extends LitElement {
  @state() private busy = false
  @state() private refreshing = false
  @state() private message: string | null = null
  @state() private online = navigator.onLine
  @state() private pullProgress = 0

  private readonly onOnline = (): void => {
    this.online = true
  }
  private readonly onOffline = (): void => {
    this.online = false
  }

  private touchStartY = 0
  private pulling = false
  private readonly PULL_THRESHOLD = 60

  static override styles = css`
    :host {
      display: block;
    }
    /* Shadow DOM doesn't inherit the global .material-icons font, so declare it
       here or the icon ligatures (e.g. "check") render as plain text. */
    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      font-feature-settings: 'liga';
      -webkit-font-feature-settings: 'liga';
    }
    .section-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 18px;
      font-weight: 600;
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 16px 0;
    }
    /* Thin top progress bar, matching the trip detail / conditions refresh. */
    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      display: block;
      width: 100%;
      margin: 0 0 12px;
    }
    .plan-card {
      position: relative;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 12px;
      padding: 16px;
      background: var(--cairn-surface-color, #ffffff);
    }
    .refresh-btn {
      position: absolute;
      top: 12px;
      right: 12px;
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      color: var(--cairn-text-secondary-color, #64748b);
      transition:
        background 0.15s,
        color 0.15s;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .refresh-btn:hover {
      background: var(--cairn-border-color, #e2e8f0);
      color: var(--cairn-text-color, #1e293b);
    }
    .refresh-btn[disabled] {
      opacity: 0.4;
      cursor: default;
    }
    .refresh-btn .material-icons {
      font-size: 20px;
    }
    .refresh-btn.spinning .material-icons {
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
    .pull-indicator {
      display: flex;
      justify-content: center;
      overflow: hidden;
      transition: height 0.2s;
    }
    .pull-indicator .material-icons {
      color: var(--cairn-text-secondary-color, #64748b);
      transition: transform 0.2s;
    }
    .plan-head {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .plan-name {
      font-size: 20px;
      font-weight: 700;
      color: var(--cairn-text-color, #1e293b);
    }
    .plan-badge {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--cairn-success-color, #16a34a);
      border: 1px solid var(--cairn-success-color, #16a34a);
      border-radius: 999px;
      padding: 1px 8px;
    }
    .plan-tagline {
      margin: 4px 0 12px;
      color: var(--cairn-text-secondary-color, #64748b);
      font-size: 14px;
    }
    .renewal {
      display: flex;
      align-items: center;
      gap: 6px;
      margin: -6px 0 12px;
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
    }
    .renewal.warn {
      color: var(--cairn-warning-color, #b45309);
    }
    .renewal .material-icons {
      font-size: 16px;
    }
    .features {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .features li {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: var(--cairn-text-color, #1e293b);
    }
    .features .material-icons {
      font-size: 18px;
      color: var(--cairn-success-color, #16a34a);
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-top: 16px;
    }
    .btn {
      padding: 11px 18px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid transparent;
    }
    .btn[disabled] {
      opacity: 0.6;
      cursor: default;
    }
    .btn.primary {
      background: var(--cairn-primary-color, #2d3748);
      color: #fff;
    }
    .btn.ghost {
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
      border-color: var(--cairn-border-color, #e2e8f0);
    }
    .message {
      margin: 12px 0 0;
      font-size: 13px;
      color: var(--cairn-text-secondary-color, #64748b);
    }
    .helper {
      margin: 10px 0 0;
      font-size: 12px;
      color: var(--cairn-text-secondary-color, #64748b);
    }
  `

  override connectedCallback(): void {
    super.connectedCallback()
    window.addEventListener('online', this.onOnline)
    window.addEventListener('offline', this.onOffline)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    window.removeEventListener('online', this.onOnline)
    window.removeEventListener('offline', this.onOffline)
  }

  private onTouchStart = (e: TouchEvent): void => {
    if (!Capacitor.isNativePlatform()) return
    this.touchStartY = e.touches[0].clientY
    this.pulling = true
  }

  private onTouchMove = (e: TouchEvent): void => {
    if (!this.pulling || this.refreshing) return
    const dy = e.touches[0].clientY - this.touchStartY
    if (dy > 0) {
      this.pullProgress = Math.min(dy / this.PULL_THRESHOLD, 1)
    }
  }

  private onTouchEnd = (): void => {
    if (!this.pulling) return
    this.pulling = false
    if (this.pullProgress >= 1) {
      void this.refreshSubscription()
    }
    this.pullProgress = 0
  }

  private async refreshSubscription(): Promise<void> {
    if (this.refreshing || !this.online) return
    this.refreshing = true
    this.message = null
    const prevTier = authService.getState().planTier ?? 'free'
    try {
      await authService.refreshSession()
      const newTier = authService.getState().planTier ?? 'free'
      if (newTier !== prevTier) {
        this.message = `Plan updated to ${TIER_INFO[newTier].name}.`
      } else {
        this.message = 'Subscription status is up to date.'
      }
    } catch {
      this.message = 'Could not refresh — try again in a moment.'
    }
    this.refreshing = false
    this.requestUpdate()
  }

  private async openPaywall(): Promise<void> {
    this.busy = true
    this.message = null
    const url = await paywallUrl()
    this.busy = false
    if (!url) {
      this.message = 'Plans are unavailable right now. Please try again in a moment.'
      return
    }
    await openExternal(url, false)
  }

  private async openManage(): Promise<void> {
    this.busy = true
    this.message = null
    const res = await manageUrl()
    this.busy = false
    if (res.ok) {
      await openExternal(res.url, true)
      return
    }
    this.message =
      res.reason === 'no_subscription'
        ? 'No billable subscription to manage — your access may be complimentary or managed elsewhere.'
        : 'The management portal is unavailable right now. Please try again in a moment.'
  }

  /** Friendly long date (e.g. "June 7, 2026"), or null if unparseable. */
  private formatDate(iso: string): string | null {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
  }

  /**
   * The one-line renewal status under the plan name, derived from tier + next +
   * renewsAt. Returns null when there's nothing to say — free tier, or a token
   * predating the renewal claims.
   *   no end date          → "Unlimited {Name} — never expires"  (comp/lifetime grant)
   *   next === tier        → "Renews on {date}"
   *   next === 'free'      → "Ends on {date}"     (cancelled / finite promo)
   *   next is a lower tier → "Switches to {Name} on {date}"  (scheduled downgrade)
   */
  private renewal(tier: PlanTier): { text: string; warn: boolean; icon: string } | null {
    if (tier === 'free') return null
    const { planRenewsAt, planNext } = authService.getState()
    // Paid tier with no period-end date = an unlimited/comp grant (a real
    // subscription always carries an end date), so nothing renews or expires.
    if (!planRenewsAt) {
      return {
        text: `Unlimited ${TIER_INFO[tier].name} — never expires`,
        warn: false,
        icon: 'all_inclusive',
      }
    }
    const when = this.formatDate(planRenewsAt)
    if (!when) return null
    if (planNext && planNext !== 'free' && planNext !== tier) {
      return {
        text: `Switches to ${TIER_INFO[planNext].name} on ${when}`,
        warn: true,
        icon: 'schedule',
      }
    }
    if (planNext === 'free') return { text: `Ends on ${when}`, warn: true, icon: 'schedule' }
    return { text: `Renews on ${when}`, warn: false, icon: 'autorenew' }
  }

  override render(): unknown {
    const tier: PlanTier = authService.getState().planTier ?? 'free'
    const info = TIER_INFO[tier]
    const isPaid = tier !== 'free'
    const isLifetime = isPaid && !authService.getState().planRenewsAt
    const renewal = this.renewal(tier)
    const pullHeight = Math.round(this.pullProgress * 40)
    const pullRotation = Math.round(this.pullProgress * 180)
    return html`
      <h2 class="section-title">
        <span class="material-icons">card_membership</span>
        Subscription
      </h2>

      ${this.refreshing
        ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
        : nothing}
      ${pullHeight > 0
        ? html`<div class="pull-indicator" style="height:${pullHeight}px">
            <span class="material-icons" style="transform:rotate(${pullRotation}deg)">refresh</span>
          </div>`
        : nothing}

      <div
        class="plan-card"
        @touchstart=${this.onTouchStart}
        @touchmove=${this.onTouchMove}
        @touchend=${this.onTouchEnd}
      >
        <button
          class="refresh-btn"
          ?disabled=${this.refreshing || !this.online}
          @click=${this.refreshSubscription}
          title="Refresh subscription status"
        >
          <span class="material-icons">refresh</span>
        </button>
        <div class="plan-head">
          <span class="plan-name">${info.name}</span>
          ${isPaid
            ? html`<span class="plan-badge">${isLifetime ? 'Lifetime' : 'Active'}</span>`
            : nothing}
        </div>
        <p class="plan-tagline">${info.tagline}</p>
        ${renewal
          ? html`<p class="renewal ${renewal.warn ? 'warn' : ''}">
              <span class="material-icons">${renewal.icon}</span>
              ${renewal.text}
            </p>`
          : nothing}
        <ul class="features">
          ${info.features.map(f => html`<li><span class="material-icons">check</span>${f}</li>`)}
        </ul>
      </div>

      ${this.message ? html`<p class="message">${this.message}</p>` : nothing}

      <div class="actions">
        ${isPaid
          ? html`<button
              class="btn primary"
              ?disabled=${this.busy || !this.online || isLifetime}
              @click=${this.openManage}
              title=${isLifetime
                ? 'Your access is complimentary — there is no billable subscription to manage.'
                : 'Open the billing portal'}
            >
              ${this.busy ? 'Opening…' : 'Manage subscription'}
            </button>`
          : html`<button
              class="btn primary"
              ?disabled=${this.busy || !this.online}
              @click=${this.openPaywall}
            >
              ${this.busy ? 'Opening…' : 'See plans & upgrade'}
            </button>`}
      </div>

      <p class="helper">
        ${!this.online
          ? `You're offline — your ${info.name} features still work. Plan changes need a connection.`
          : isLifetime
            ? `Your ${info.name} access is complimentary and never expires — there's nothing to manage.`
            : isPaid
              ? 'Change your tier, update payment, or cancel anytime in the billing portal.'
              : 'Subscriptions are billed through our secure web checkout.'}
      </p>
    `
  }
}
